/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 * Based on https://www.npmjs.org/package/shape2d but modified to be without requires
 */


import {vec2} from "gl-matrix";

export default
class Shape2D {

    public steps: number = 1;
    // If step is not provided to a ***CurveTo function,
    // then it will be approximated with a very simple distance check
    public approximateCurves: boolean = true;
    public approximationFactor: number = 0.5;

    private points: vec2[] = [];

    private _move: vec2 = vec2.create();
    private _start: vec2 = vec2.create();
    private _hasMoved: boolean = false;
    private _newPath: boolean = true;

    public reset(): void {
        this.points.length = 0;
        this._newPath = true;
        this._hasMoved = false;
        this._move[0] = this._move[1] = 0;
        this._start[0] = this._start[1] = 0;
    }

    public beginPath() {
        this.reset();
    }

    public moveTo(arg, steps): void {
        let x = arg.x;
        let y = arg.y;

        this._newPath = true;
        this._move[0] = x;
        this._move[1] = y;
        this._start[0] = x;
        this._start[1] = y;
        this._hasMoved = true;
    }

    private __newPoint(nx, ny): void {
        this.points.push(vec2.fromValues(nx, ny));
        this._newPath = false;
    }

    /** Closes the path by performing a lineTo with the first 'starting' point.
     If the path is empty, this does nothing. */
    public closePath(steps): void {
        if (this.points.length === 0)
            return;

        this.lineTo({x: this._start[0], y: this._start[1]}, steps);
    }

    public lineTo(arg, steps): void {
        let x = arg.x;
        let y = arg.y;

        //if we are calling lineTo before any moveTo.. make this the first point
        if (!this._hasMoved) {
            this.moveTo({x: x, y: y}, steps);
            return;
        }

        steps = Math.max(1, steps || this.steps);
        for (let i = 0; i <= steps; i++) {
            if (!this._newPath && i == 0)
                continue;

            let t = i / steps;
            let np = vec2.create();
            vec2.lerp(np, this._move, vec2.fromValues(x, y), t);

            this.__newPoint(np[0], np[1]);
        }
        this._move[0] = x;
        this._move[1] = y;
    }

    /** Creates a bezier (cubic) curve to the specified point, with the given control points.
     If steps is not specified or is a falsy value, this function will use the default value
     set for this Path object. It will be capped to a minimum of 3 steps.
     */
    public bezierCurveTo(arg, steps): void {
        let x2 = arg.x2;
        let y2 = arg.y2
        let x3 = arg.x1
        let y3 = arg.y1;
        let x4 = arg.x;
        let y4 = arg.y;

        //if we are calling bezierCurveTo before any moveTo.. the control point takes the lead
        //this is how it works with HTML5 canvas context
        if (!this._hasMoved) {
            this.moveTo({x: x2, y: y2}, steps);
        }

        let x1 = this._move[0];
        let y1 = this._move[1];

        //try to approximate with a simple distance sum.
        //more accurate would be to use this:
        //http://antigrain.com/research/adaptive_bezier/
        if (!steps) {
            if (this.approximateCurves) {
                let d1 = vec2.distance(vec2.fromValues(x1, y1), vec2.fromValues(x2, y2));
                let d2 = vec2.distance(vec2.fromValues(x2, y2), vec2.fromValues(x3, y3));
                let d3 = vec2.distance(vec2.fromValues(x3, y3), vec2.fromValues(x4, y4));
                steps = ~~((d1 + d2 + d3) * this.approximationFactor);
            } else {
                steps = Math.max(1, this.steps);
            }
        }

        for (let i = 0; i < steps; i++) {
            let t = i / (steps - 1);
            let dt = (1 - t);

            let dt2 = dt * dt;
            let dt3 = dt2 * dt;
            let t2 = t * t;
            let t3 = t2 * t;

            let x = dt3 * x1 + 3 * dt2 * t * x2 + 3 * dt * t2 * x3 + t3 * x4;
            let y = dt3 * y1 + 3 * dt2 * t * y2 + 3 * dt * t2 * y3 + t3 * y4;

            this.__newPoint(x, y);
        }

        this._move[0] = x4;
        this._move[1] = y4;
    }

    /** Creates a quadratic curve to the specified point, with the given control points.
     If steps is not specified or is a falsy value, this function will use the default value
     set for this Path object. It will be capped to a minimum of 3 steps.
     */
    public quadraticCurveTo(arg, steps): void {
        let x2 = arg.x1;
        let y2 = arg.y1
        let x3 = arg.x;
        let y3 = arg.y;

        //parity with HTML5 canvas context, just move to first control point
        if (!this._hasMoved) {
            this.moveTo({x: x2, y: y2}, steps);
        }

        let x1 = this._move[0];
        let y1 = this._move[1];

        //try to approximate with a simple distance sum.
        //more accurate would be to use this:
        //http://antigrain.com/research/adaptive_bezier/
        if (!steps) {
            if (this.approximateCurves) {
                let d1 = vec2.distance(vec2.fromValues(x1, y1), vec2.fromValues(x2, y2));
                let d2 = vec2.distance(vec2.fromValues(x2, y2), vec2.fromValues(x3, y3));
                steps = ~~((d1 + d2) * this.approximationFactor);
            } else {
                steps = Math.max(1, this.steps);
            }
        }

        for (let i = 0; i < steps; i++) {
            let t = i / (steps - 1);
            let dt = (1 - t);
            let dtSq = dt * dt;
            let tSq = t * t;

            let x = dtSq * x1 + 2 * dt * t * x2 + tSq * x3;
            let y = dtSq * y1 + 2 * dt * t * y2 + tSq * y3;

            this.__newPoint(x, y);
        }

        this._move[0] = x3;
        this._move[1] = y3;
    }

    public calculateBoundingBox(): any {
        let points = this.points;

        let minX = Number.MAX_VALUE,
            minY = Number.MAX_VALUE,
            maxX = -Number.MAX_VALUE,
            maxY = -Number.MAX_VALUE;

        for (let i = 0; i < points.length; i++) {
            let p = points[i];

            minX = Math.min(minX, p[0]);
            minY = Math.min(minY, p[1]);
            maxX = Math.max(maxX, p[0]);
            maxY = Math.max(maxY, p[1]);
        }

        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        }
    }

    public contains(x: any, y: any): boolean {
        let testx = x, testy = y;
        if (typeof x === "object") {
            testx = x[0];
            testy = x[1];
        }

        let points = this.points;
        let nvert = points.length;
        let i, j, c = false;
        for (i = 0, j = nvert - 1; i < nvert; j = i++) {
            if (((points[i][1] > testy) != (points[j][1] > testy)) &&
                (testx < (points[j][0] - points[i][0]) * (testy - points[i][1]) / (points[j][1] - points[i][1]) + points[i][0])) {
                c = !c;
            }
        }
        return c;
    }

    public simplify(tolerance: any, out: any): any {
        let points = this.points,
            len = points.length,
            point = vec2.create(),
            sqTolerance = tolerance * tolerance,
            prevPoint = vec2.clone(points[0]);

        if (!out)
            out = new Shape2D();

        let outPoints = [];
        outPoints.push(prevPoint);

        for (let i = 1; i < len; i++) {
            point = points[i];
            if (vec2.squaredDistance(point, prevPoint) > sqTolerance) {
                outPoints.push(vec2.clone(point));
                prevPoint = point;
            }
        }

        if (prevPoint[0] !== point[0] || prevPoint[1] !== point[1]) {
            outPoints.push(vec2.clone(point));
        }

        out.points = outPoints;
        return out;
    }
}
